import React from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const RISK_COLORS = {
  Low: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  Medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  High: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

const STAGE_COLORS = {
  'Seed': 'bg-purple-50 text-purple-700',
  'Series A': 'bg-blue-50 text-blue-700',
  'Series B': 'bg-indigo-50 text-indigo-700',
  'Growth': 'bg-teal-50 text-teal-700',
  'Pre-IPO': 'bg-orange-50 text-orange-700',
};

export default function OpportunityCard({ opportunity, viewMode, onSave, onExpressInterest, onClick }) {
  const risk = RISK_COLORS?.[opportunity?.riskLevel] || RISK_COLORS?.Medium;
  const stageClass = STAGE_COLORS?.[opportunity?.fundingStage] || 'bg-gray-50 text-gray-700';
  const isListView = viewMode === 'list';

  return (
    <div
      className={`bg-white rounded-xl border overflow-hidden cursor-pointer group card-hover animate-fade-in-up ${isListView ? 'flex flex-row' : 'flex flex-col'}`}
      style={{ borderColor: opportunity?.isWinPick ? 'var(--color-accent)' : 'var(--color-border)', borderTopWidth: opportunity?.isWinPick ? '3px' : '1px' }}
      onClick={() => onClick(opportunity)}
    >
      {/* Image */}
      <div className={`overflow-hidden flex-shrink-0 ${isListView ? 'w-32 md:w-48' : 'h-44'}`}>
        <Image
          src={opportunity?.image}
          alt={opportunity?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      {/* Content */}
      <div className="flex flex-col flex-1 p-4">
        {/* Badges row */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {opportunity?.isFeaturedFounder && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'linear-gradient(90deg,#D4A520,#F0C842)', color: '#0B1F3B' }}>
              <Icon name="Ribbon" size={10} color="#0B1F3B" />
              Featured Founder
            </span>
          )}
          {opportunity?.isWinPick && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'var(--color-accent)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>
              <Icon name="Star" size={10} color="var(--color-primary)" />
              WIN Pick
            </span>
          )}
          {opportunity?.isWomenLed && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border" style={{ background: 'rgba(133,113,77,0.1)', color: 'var(--color-accent)', borderColor: 'var(--color-accent)', fontFamily: 'var(--font-caption)' }}>
              <Icon name="Crown" size={10} color="var(--color-accent)" />
              Women-Led
            </span>
          )}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stageClass}`} style={{ fontFamily: 'var(--font-caption)' }}>
            {opportunity?.fundingStage}
          </span>
        </div>

        {/* Title & Sector */}
        <div className="mb-2">
          <h3 className="font-heading font-semibold text-base leading-tight line-clamp-1 group-hover:text-yellow-600 transition-colors" style={{ color: 'var(--color-foreground)' }}>
            {opportunity?.name}
          </h3>
          <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            {opportunity?.sector}
          </p>
        </div>

        {/* Description */}
        {!isListView && (
          <p className="font-caption text-sm line-clamp-2 mb-3" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            {opportunity?.description}
          </p>
        )}

        {/* Stats */}
        <div className={`grid gap-2 mb-3 ${isListView ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <div>
            <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Min. Investment</p>
            <p className="font-data font-semibold text-sm whitespace-nowrap" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>
              ${opportunity?.minInvestment?.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Projected ROI</p>
            <p className="font-data font-bold text-sm" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-data)' }}>
              {opportunity?.projectedROI}%
            </p>
          </div>
          {!isListView && (
            <div>
              <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Risk</p>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${risk?.bg} ${risk?.text} ${risk?.border}`} style={{ fontFamily: 'var(--font-caption)' }}>
                {opportunity?.riskLevel}
              </span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {!isListView && (
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Funding Progress</span>
              <span className="font-caption text-xs font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{opportunity?.fundingProgress}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${opportunity?.fundingProgress}%`, background: 'var(--color-accent)' }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-auto" onClick={e => e?.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExpressInterest(opportunity)}
            className="flex-1"
          >
            Express Interest
          </Button>
          <button
            onClick={() => onSave(opportunity?.id)}
            className="w-9 h-9 flex items-center justify-center rounded-md border transition-all duration-200 flex-shrink-0"
            style={{ borderColor: opportunity?.isSaved ? 'var(--color-accent)' : 'var(--color-border)', background: opportunity?.isSaved ? 'rgba(133,113,77,0.1)' : 'white' }}
            aria-label={opportunity?.isSaved ? 'Remove from saved' : 'Save opportunity'}
          >
            <Icon name={opportunity?.isSaved ? 'Bookmark' : 'BookmarkPlus'} size={16} color={opportunity?.isSaved ? 'var(--color-accent)' : 'var(--color-muted-foreground)'} />
          </button>
        </div>
      </div>
    </div>
  );
}